import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import { SubscriptionPlan } from "@/hooks/use-subscription";

interface PlanCardProps {
  plan: SubscriptionPlan;
  currentPlanId?: number;
  onSelect: (planId: number) => void;
  isPending?: boolean;
  className?: string;
  featured?: boolean;
  disabled?: boolean;
}

export function PlanCard({ 
  plan, 
  currentPlanId, 
  onSelect, 
  isPending,
  className,
  featured = false,
  disabled = false
}: PlanCardProps) {
  const isCurrentPlan = currentPlanId === plan.id;
  
  return (
    <Card 
      className={cn(
        "flex flex-col h-full",
        featured && "border-primary/50 shadow-lg shadow-primary/10",
        className
      )}
    >
      <CardHeader className={cn(
        "pb-4",
        featured && "bg-primary-50 dark:bg-primary-950/30"
      )}>
        <CardTitle className="flex items-center justify-between">
          <span>{plan.name}</span>
          {featured && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-300">
              Popular
            </span>
          )}
        </CardTitle>
        <div className="mt-2">
          <span className="text-3xl font-bold">{formatCurrency(plan.price)}</span>
          <span className="text-neutral-500 ml-1">/month</span>
        </div>
      </CardHeader>
      <CardContent className="flex-grow pt-4">
        <ul className="space-y-3">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
              <span className="ml-2 text-sm text-neutral-600 dark:text-neutral-400">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter className="pt-4 pb-6">
        <Button 
          className="w-full" 
          onClick={() => onSelect(plan.id)}
          disabled={isCurrentPlan || isPending || disabled}
          variant={featured ? "default" : "outline"}
        >
          {isPending ? (
            "Processing..."
          ) : isCurrentPlan ? (
            "Current Plan"
          ) : (
            plan.price > 0 ? "Upgrade" : "Select Plan"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
